import api from './api';
import { LoginCredentials, AuthResponse, User } from '../types/auth.types';
import { ApiResponse } from '../types/common.types';

class AuthService {
  /**
   * Login user
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/admin/login', credentials);
    return response.data;
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await api.post('/admin/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearAuthData();
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<string> {
    const response = await api.post('/admin/refresh-token', { refreshToken });
    return response.data.data.accessToken;
  }

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<User> {
    const response = await api.get<ApiResponse<{ user: User }>>('/admin/me');
    return response.data.data!.user;
  }

  /**
   * Save auth data to local storage
   */
  saveAuthData(accessToken: string, refreshToken: string, user: User): void {
    try {
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      console.log('✅ Auth data saved to localStorage');
    } catch (error) {
      console.error('❌ Failed to save auth data:', error);
    }
  }

  /**
   * Get auth data from local storage
   */
  getAuthData(): { accessToken: string | null; refreshToken: string | null; user: User | null } {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      return { accessToken, refreshToken, user };
    } catch (error) {
      console.error('❌ Failed to get auth data:', error);
      return { accessToken: null, refreshToken: null, user: null };
    }
  }

  /**
   * Clear auth data
   */
  clearAuthData(): void {
    try {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      console.log('✅ Auth data cleared from localStorage');
    } catch (error) {
      console.error('❌ Failed to clear auth data:', error);
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const { accessToken } = this.getAuthData();
    return !!accessToken;
  }

  /**
   * Check if token is expired (simple check)
   */
  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiryTime = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= expiryTime;
    } catch (error) {
      return true; // If can't parse, assume expired
    }
  }
}

export default new AuthService();